// KPI Glossary — static data derived from schema/metrics.yaml

export type KPILevel = 'stored' | 'computed' | 'long_term';
export type KPIDomain = 'casino' | 'sport' | 'finance' | 'player' | 'cross';

export interface KPIEntry {
  id: string;          // e.g. KPI-C01
  name: string;        // human-readable
  metric: string;      // technical metric name
  domain: KPIDomain;
  category: string;
  level: KPILevel;
  formula?: string;
  description: string;
  variants?: string;   // "real / bonus" or undefined
  currencies?: string; // "native, EUR, USD" or undefined
  sourceTable: string;
  dwhMapping?: string;
  notes?: string;
}

export const KPI_CATEGORIES = [
  'Revenue',
  'Turnover & Volume',
  'Players',
  'Deposits',
  'Withdrawals',
  'Bonus',
  'Performance & Margins',
  'Lifetime & Retention',
] as const;

export type KPICategory = typeof KPI_CATEGORIES[number];

export const DOMAIN_LABELS: Record<KPIDomain, string> = {
  casino: 'Casino',
  sport: 'Sportsbook',
  finance: 'Finance',
  player: 'Player',
  cross: 'Cross-Product',
};

export const KPI_GLOSSARY: KPIEntry[] = [
  // ===== REVENUE =====
  {
    id: 'KPI-C01',
    name: 'Casino GGR',
    metric: 'casino_ggr',
    domain: 'casino',
    category: 'Revenue',
    level: 'stored',
    formula: 'bet_amount_real - void_amount_real - win_amount_real',
    description: 'Gross Gaming Revenue for casino products (slots, table, live, crash). Calculated separately for real and bonus funds.',
    variants: 'real / bonus',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_casino_daily, agg_financial_kpi_daily',
    dwhMapping: 'CasinoNetAmount (real+bonus mixed)',
  },
  {
    id: 'KPI-S01',
    name: 'Sportsbook GGR',
    metric: 'sport_ggr',
    domain: 'sport',
    category: 'Revenue',
    level: 'stored',
    formula: 'bet_amount_real - win_amount_real - cancelled_amount_real',
    description: 'Gross Gaming Revenue for sportsbook. Includes cancelled bets as a deduction (unlike casino which uses voids).',
    variants: 'real / bonus',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_sportsbook_daily, agg_financial_kpi_daily',
    dwhMapping: 'SportNetAmount (real+bonus mixed)',
  },
  {
    id: 'KPI-X01',
    name: 'Total GGR',
    metric: 'ggr_total',
    domain: 'cross',
    category: 'Revenue',
    level: 'stored',
    formula: 'casino_ggr + sport_ggr',
    description: 'Combined GGR across all products. Primary top-line revenue metric.',
    variants: 'real / bonus',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_financial_kpi_daily',
  },
  {
    id: 'KPI-C02',
    name: 'Casino NGR',
    metric: 'ngr_casino',
    domain: 'casino',
    category: 'Revenue',
    level: 'stored',
    formula: 'casino_ggr_real - bonus_cost_casino - brand_fees_casino - payment_fees_casino - taxes_casino',
    description: 'Net Gaming Revenue for casino. GGR minus all costs (bonus, brand fees, payment fees, taxes). Uses real GGR only.',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_financial_kpi_daily',
  },
  {
    id: 'KPI-S02',
    name: 'Sportsbook NGR',
    metric: 'ngr_sport',
    domain: 'sport',
    category: 'Revenue',
    level: 'stored',
    formula: 'sport_ggr_real - bonus_cost_sport - odds_brand_fee - payment_fees_sport',
    description: 'Net Gaming Revenue for sportsbook. Includes odds brand fee (Betradar/Oddin) as a cost.',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_financial_kpi_daily',
  },
  {
    id: 'KPI-X02',
    name: 'Total NGR',
    metric: 'ngr_total',
    domain: 'cross',
    category: 'Revenue',
    level: 'stored',
    formula: 'ngr_casino + ngr_sport',
    description: 'Combined Net Gaming Revenue. The true bottom-line revenue after all costs.',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_financial_kpi_daily',
  },
  {
    id: 'KPI-X05',
    name: 'Net Cash',
    metric: 'net_cash',
    domain: 'cross',
    category: 'Revenue',
    level: 'stored',
    formula: 'total_deposits_sum - withdrawal_sum',
    description: 'Net player cash flow. Positive = more deposits than withdrawals.',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_financial_kpi_daily',
  },

  // ===== TURNOVER & VOLUME =====
  {
    id: 'KPI-C27',
    name: 'Casino Turnover',
    metric: 'turnover',
    domain: 'casino',
    category: 'Turnover & Volume',
    level: 'stored',
    formula: 'bet_amount - void_amount',
    description: 'Total wagered volume net of voids. Measures betting activity regardless of outcomes.',
    variants: 'real / bonus',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_casino_daily',
  },
  {
    id: 'KPI-C25',
    name: 'Casino Bet Count',
    metric: 'bet_count',
    domain: 'casino',
    category: 'Turnover & Volume',
    level: 'stored',
    description: 'Number of casino bets placed (real and bonus separately).',
    variants: 'real / bonus',
    sourceTable: 'agg_casino_daily',
    dwhMapping: 'TotalBetNumber',
  },
  {
    id: 'KPI-S26',
    name: 'Sport Bet Count',
    metric: 'sport_bet_count',
    domain: 'sport',
    category: 'Turnover & Volume',
    level: 'stored',
    description: 'Number of sportsbook bets placed.',
    variants: 'real / bonus',
    sourceTable: 'agg_sportsbook_daily',
  },

  // ===== PLAYERS =====
  {
    id: 'KPI-C03',
    name: 'Active Players',
    metric: 'active_players',
    domain: 'cross',
    category: 'Players',
    level: 'stored',
    description: 'Distinct players with any activity (bet, deposit, login) in the period. Uses uniqMerge for cross-period deduplication.',
    sourceTable: 'agg_player_activity_daily, agg_financial_kpi_daily',
    dwhMapping: 'ActivePlayers',
    notes: 'uniq aggregation ensures accurate dedup across days',
  },
  {
    id: 'KPI-C10',
    name: 'Confirmed Registrations',
    metric: 'confirmed_registrations',
    domain: 'player',
    category: 'Players',
    level: 'stored',
    description: 'New player registrations that completed verification (email/KYC).',
    sourceTable: 'agg_financial_kpi_daily',
  },
  {
    id: 'KPI-C18',
    name: 'Depositing Players',
    metric: 'depositing_players',
    domain: 'finance',
    category: 'Players',
    level: 'stored',
    description: 'Distinct players who made at least one deposit in the period.',
    sourceTable: 'agg_financial_kpi_daily',
    notes: 'uniq aggregation',
  },
  {
    id: 'KPI-C11',
    name: 'Conversion Rate',
    metric: 'conversion_rate',
    domain: 'player',
    category: 'Players',
    level: 'computed',
    formula: 'ftd_count / registrations × 100',
    description: 'Percentage of registered players who made their first deposit. Key acquisition funnel metric.',
    sourceTable: 'agg_financial_kpi_daily',
    notes: 'Returns null if registrations = 0',
  },
  {
    id: 'KPI-X04',
    name: 'Cross-Sell Rate',
    metric: 'cross_sell_rate',
    domain: 'cross',
    category: 'Players',
    level: 'computed',
    formula: 'players_both_products / active_players × 100',
    description: 'Percentage of active players who play both casino and sportsbook.',
    sourceTable: 'computed (cross-table)',
  },

  // ===== DEPOSITS =====
  {
    id: 'KPI-C13',
    name: 'Total Deposits Sum',
    metric: 'total_deposits_sum',
    domain: 'finance',
    category: 'Deposits',
    level: 'stored',
    formula: 'deposits_sum - chargebacks_sum - refunds_sum',
    description: 'Net deposit amount after chargebacks and refunds. Wallet is unified (no casino/sport split).',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_deposit_daily, agg_financial_kpi_daily',
    dwhMapping: 'DepositAmount',
  },
  {
    id: 'KPI-C14',
    name: 'Total Deposits Count',
    metric: 'total_deposits_count',
    domain: 'finance',
    category: 'Deposits',
    level: 'stored',
    formula: 'deposits_count - chargebacks_count - refunds_count',
    description: 'Net number of deposits after chargebacks and refunds.',
    sourceTable: 'agg_financial_kpi_daily',
    dwhMapping: 'DepositNumber',
  },
  {
    id: 'KPI-C15',
    name: 'FTD Count',
    metric: 'ftd_count',
    domain: 'finance',
    category: 'Deposits',
    level: 'stored',
    description: 'Number of first-time deposits. Critical for acquisition analysis.',
    sourceTable: 'agg_deposit_daily, agg_financial_kpi_daily',
    dwhMapping: 'FtdNumber',
  },
  {
    id: 'KPI-C16',
    name: 'FTD Sum',
    metric: 'ftd_sum',
    domain: 'finance',
    category: 'Deposits',
    level: 'stored',
    description: 'Total amount of first-time deposits.',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_financial_kpi_daily',
    dwhMapping: 'FtdAmount',
  },
  {
    id: 'KPI-C17',
    name: 'Average FTD',
    metric: 'avg_ftd',
    domain: 'finance',
    category: 'Deposits',
    level: 'computed',
    formula: 'ftd_sum / ftd_count',
    description: 'Average first-time deposit amount.',
    sourceTable: 'agg_financial_kpi_daily',
  },
  {
    id: 'KPI-C19',
    name: 'Avg Deposit Count per Player',
    metric: 'avg_deposit_count',
    domain: 'finance',
    category: 'Deposits',
    level: 'computed',
    formula: 'total_deposits_count / depositing_players',
    description: 'Average number of deposits per depositing player.',
    sourceTable: 'agg_financial_kpi_daily',
  },
  {
    id: 'KPI-C20',
    name: 'Avg Deposit Amount',
    metric: 'avg_deposit_sum',
    domain: 'finance',
    category: 'Deposits',
    level: 'computed',
    formula: 'total_deposits_sum / total_deposits_count',
    description: 'Average deposit amount across all deposits.',
    sourceTable: 'agg_financial_kpi_daily',
  },

  // ===== WITHDRAWALS =====
  {
    id: 'KPI-C21',
    name: 'Withdrawal Sum',
    metric: 'withdrawal_sum',
    domain: 'finance',
    category: 'Withdrawals',
    level: 'stored',
    formula: 'withdrawals_sum - cancelled_withdrawals_sum',
    description: 'Net withdrawal amount after cancelled withdrawals.',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_withdrawal_daily, agg_financial_kpi_daily',
    dwhMapping: 'WithdrawalAmount',
  },
  {
    id: 'KPI-C22',
    name: 'Hold %',
    metric: 'hold_pct',
    domain: 'finance',
    category: 'Withdrawals',
    level: 'computed',
    formula: '(total_deposits_sum - withdrawal_sum) / total_deposits_sum × 100',
    description: 'Deposit retention rate. How much of deposited money the operator retains. Higher = more revenue retained.',
    sourceTable: 'agg_financial_kpi_daily',
    notes: 'Returns null if total_deposits_sum = 0',
  },

  // ===== BONUS =====
  {
    id: 'KPI-C23',
    name: 'Bonus Cost',
    metric: 'bonus_cost',
    domain: 'finance',
    category: 'Bonus',
    level: 'stored',
    formula: 'bonus_rw_paid_amount + bonus_freebet_amount + cashback_total - bonus_cancelled_amount',
    description: 'Actual financial cost of bonuses. This is NOT the same as ggr_bonus — bonus_cost measures the real money paid out as bonuses.',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_bonus_daily, agg_financial_kpi_daily',
    notes: 'bonus_cost ≠ ggr_bonus. ggr_bonus is revenue from bonus-funded bets; bonus_cost is actual money spent on bonuses.',
  },
  {
    id: 'KPI-C24',
    name: 'Bonus/GGR Ratio',
    metric: 'bonus_ggr_ratio',
    domain: 'finance',
    category: 'Bonus',
    level: 'computed',
    formula: 'bonus_cost / ggr_total_real × 100',
    description: 'Bonus cost as a percentage of real GGR. Tracks bonus spending efficiency.',
    sourceTable: 'agg_financial_kpi_daily',
  },
  {
    id: '',
    name: 'Bonus Efficiency',
    metric: 'bonus_efficiency',
    domain: 'finance',
    category: 'Bonus',
    level: 'computed',
    formula: 'ggr_total_bonus / bonus_cost',
    description: 'How much bonus-funded GGR each unit of bonus cost generates. Higher = bonuses drive more play.',
    sourceTable: 'agg_financial_kpi_daily',
  },

  // ===== PERFORMANCE & MARGINS =====
  {
    id: 'KPI-C28',
    name: 'GGR Margin',
    metric: 'ggr_margin',
    domain: 'casino',
    category: 'Performance & Margins',
    level: 'computed',
    formula: 'ggr_total_real / turnover_real × 100',
    description: 'GGR as a percentage of turnover. Measures house edge realization on real-money play.',
    sourceTable: 'agg_casino_daily',
    notes: 'Different from Hold % — GGR Margin is about turnover, Hold % is about deposits',
  },
  {
    id: 'KPI-C29',
    name: 'RTP Effective',
    metric: 'rtp_effective',
    domain: 'casino',
    category: 'Performance & Margins',
    level: 'computed',
    formula: 'win_amount_real / turnover_real × 100',
    description: 'Effective Return To Player. Compare with nominal RTP for anomaly detection — significant deviations may indicate issues.',
    sourceTable: 'agg_rtp_daily',
    notes: 'Available per game and per brand. Use for anomaly alerts.',
  },
  {
    id: 'KPI-C30',
    name: 'Brand Margin',
    metric: 'brand_margin',
    domain: 'casino',
    category: 'Performance & Margins',
    level: 'stored',
    formula: 'ggr_real_for_brand - brand_fee',
    description: 'Net margin after brand fee per game provider. Fee is calculated on total GGR (real + bonus).',
    currencies: 'native, EUR, USD',
    sourceTable: 'agg_brand_fee_daily',
  },
  {
    id: '',
    name: 'Net Cash Rate',
    metric: 'net_cash_rate',
    domain: 'cross',
    category: 'Performance & Margins',
    level: 'computed',
    formula: 'net_cash / total_deposits_sum × 100',
    description: 'Net cash as a percentage of deposits. Overall cash flow efficiency metric.',
    sourceTable: 'agg_financial_kpi_daily',
  },
  {
    id: 'KPI-C04',
    name: 'ARPU',
    metric: 'arpu',
    domain: 'cross',
    category: 'Performance & Margins',
    level: 'computed',
    formula: 'ngr_total / active_players',
    description: 'Average Revenue Per User. Uses NGR (not GGR) per industry standard.',
    sourceTable: 'agg_financial_kpi_daily',
    notes: 'Also available as arpu_casino and arpu_sportsbook',
  },
  {
    id: 'KPI-S06',
    name: 'ARPU Sportsbook',
    metric: 'arpu_sportsbook',
    domain: 'sport',
    category: 'Performance & Margins',
    level: 'computed',
    formula: 'ngr_sport / active_players_sportsbook',
    description: 'Average Revenue Per User for sportsbook only.',
    sourceTable: 'agg_financial_kpi_daily',
  },

  // ===== LIFETIME & RETENTION =====
  {
    id: 'KPI-C07',
    name: 'Churn Rate',
    metric: 'churn_rate',
    domain: 'player',
    category: 'Lifetime & Retention',
    level: 'long_term',
    formula: 'churned_players / start_of_period_players × 100',
    description: 'Percentage of players who were active in period N-1 but had no bets in period N.',
    sourceTable: 'computed (monthly)',
    notes: 'Calculated monthly',
  },
  {
    id: 'KPI-X06',
    name: 'Retention Rate',
    metric: 'retention_rate',
    domain: 'player',
    category: 'Lifetime & Retention',
    level: 'long_term',
    formula: '100 - churn_rate',
    description: 'Complement of churn rate. Percentage of players retained month over month.',
    sourceTable: 'computed (monthly)',
  },
  {
    id: 'KPI-C05',
    name: 'Player LTV',
    metric: 'ltv',
    domain: 'player',
    category: 'Lifetime & Retention',
    level: 'long_term',
    formula: 'arpu_monthly × avg_player_lifespan_months',
    description: 'Player Lifetime Value. Expected total revenue from a player over their entire active period.',
    sourceTable: 'computed (quarterly)',
    notes: 'Calculated quarterly for stability',
  },
  {
    id: 'KPI-C06',
    name: 'CAC',
    metric: 'cac',
    domain: 'player',
    category: 'Lifetime & Retention',
    level: 'long_term',
    formula: 'marketing_cost / new_players_with_deposit',
    description: 'Customer Acquisition Cost. Requires marketing spend as external input.',
    sourceTable: 'computed (monthly)',
    notes: 'marketing_cost is an external input, not derived from events',
  },
];

export const LEVEL_LABELS: Record<KPILevel, { label: string; description: string }> = {
  stored: {
    label: 'Stored (Gold Table)',
    description: 'Pre-aggregated in ClickHouse Gold tables using AggregateFunction columns. Retrieved via sumMerge() / uniqMerge().',
  },
  computed: {
    label: 'Computed (API)',
    description: 'Calculated at query time by the Reporting API from stored Gold table metrics. Not persisted.',
  },
  long_term: {
    label: 'Long-term',
    description: 'Calculated over extended periods (monthly/quarterly). Some require external inputs.',
  },
};
